import { Offcanvas, OffcanvasBody, OffcanvasHeader, OffcanvasProps } from 'reactstrap';
import { DashboardDivider } from '@g17eco/atoms/divider';
import { useEffect, useState } from 'react';
import IconButton from '@g17eco/molecules/button/IconButton';
import './styles.scss';
import classNames from 'classnames';
import { useChatbotVisibilityControl } from '@hooks/useChatbotVisibilityControl';

interface Props extends OffcanvasProps {
  header: string;
  isOpen: boolean;
  toggle: () => void;
  children?: JSX.Element | null;
  className?: string;
  resizable?: boolean;
}

const MIN_SIDEBAR_WIDTH = 400;

export const Sidebar = (props: Props) => {
  const { isOpen, toggle, children, header, resizable = false, ...offcanvasProps } = props;
  // This is used to delay the state in offCanvas to allow the animation to finish
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(
    resizable
      ? Math.max(MIN_SIDEBAR_WIDTH, (window.innerWidth * (0.00823 * window.innerWidth + 8.1)) / 100)
      : MIN_SIDEBAR_WIDTH,
  ); // Default width proportional to window width

  useChatbotVisibilityControl({ isHidden: isOpen });

  useEffect(() => {
    if (isOpen) {
      setIsSidebarOpen(isOpen);
      return;
    }
    // if closing then need to wait for animation to finish
    // timeout should match transition duration in css
    const timeoutId = setTimeout(() => {
      setIsSidebarOpen(isOpen);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  // Handle mouse down on resize handle
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  useEffect(() => {
    if (!resizable) {
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      const maxWidth = window.innerWidth * 0.8; // Max 80% of viewport

      if (newWidth >= MIN_SIDEBAR_WIDTH && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizable]);

  return (
    <Offcanvas
      {...offcanvasProps}
      isOpen={isSidebarOpen}
      toggle={toggle}
      className={classNames('visible', isOpen ? 'offcanvas-open' : 'offcanvas-close', offcanvasProps.className)}
      style={{
        width: `${sidebarWidth}px`,
      }}
    >
      <OffcanvasHeader
        toggle={toggle}
        close={
          <IconButton color={'secondary'} icon={'fa-light fa-sidebar-flip'} onClick={toggle} className='border-0' />
        }
      >
        {header}
      </OffcanvasHeader>
      <DashboardDivider className='mx-4' />
      <OffcanvasBody>
        <>
          {children}
          {/* Resize handle */}
          {resizable ? (
            <div
              className='resize-handle background-ThemeAccentExtralight'
              onMouseDown={handleMouseDown}
              style={{
                position: 'absolute',
                right: '-6px', // Resize only works for direction start (left side)
                top: 0,
                bottom: 0,
                width: '6px',
                cursor: 'col-resize',
                borderRight: '1px solid #aaa',
                zIndex: 1000,
              }}
            />
          ) : null}
        </>
      </OffcanvasBody>
    </Offcanvas>
  );
};
